/* eslint-disable @typescript-eslint/no-unused-vars */

import { inject, Injectable } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { LocalScanningWebApiService } from '../../services/local-scanning-web-api.service';
import { DiagnosticResultsReceived, ResultsActions, ResultsDataError, ResultsDataReceived, } from './results.actions';
import { IPayloadAction } from '../scanner/scanner.actions';
import { Observable, of } from 'rxjs';
import { catchError, map, pluck, switchMap, withLatestFrom } from 'rxjs/operators';

@Injectable()
export class ResultsEffects {
  private actions$ = inject(Actions);
  private store$ = inject<Store<any>>(Store);
  private webapi = inject(LocalScanningWebApiService);

  getDiagnosticResults$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(ResultsActions.GET_DIAGNOSTIC_RESULTS),
    switchMap((action: IPayloadAction) => {
      return this.webapi.getResultsBySessionId(action.payload.sessionId, action.payload.populateAllFields)
        .pipe(
          map(results => {
            return {
              sessionId: action.payload.sessionId,
              results,
            };
          }),
          catchError(e => {
            return of(new ResultsDataError(e));
          })
        );
    }),
    switchMap((result: any) => {
      if (result.type === ResultsActions.RESULTS_ERROR) {
        return of(result);
      }
      return of(
        new DiagnosticResultsReceived(result),
      );
    }),
  ));

  updateResultsStatus$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(ResultsActions.UPDATE_RESULTS_STATUS),
    withLatestFrom(this.store$.select('session').pipe(pluck('id'))),
    switchMap(([action, sessionId]) => {
      return this.webapi.getResultsBySessionId(sessionId)
        .pipe(
          // catch here; a catch on the main observable chain
          // would "complete" the observable and unsubscribe the effect
          catchError(e => of(new ResultsDataError(e)))
        );
    }),
    switchMap((result: any) => {
      if (result.type === ResultsActions.RESULTS_ERROR) {
        return of(result);
      }
      return of(new ResultsDataReceived(result));
    })
  ));

  expireProcessingResults$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(ResultsActions.EXPIRE_PROCESSING_RESULTS),
    withLatestFrom(this.store$.select('scans'), this.store$.select('results')),
    switchMap(([action, scans, results]) => {

      const duplicateDocuments = results.current
        .filter((document) => {
          return document.scanIds.length > 8;
        })
        .reduce((acc, document) => {
          const count = Math.ceil(document.scanIds.length / 8);
          return [
            ...acc,
            ...Array(count).fill({
              lithocode: document.lithocode,
              sortKey: `duplicate-${document.lithocode}`,
              error: 'was a duplicate document.',
            }),
          ];
        }, []);

      const duplicateDocumentCount = duplicateDocuments.length;

      const totalDocsExpected = Math.ceil(scans.totalScans / 8);
      const documentsFound = results.successful.length + results.failures.length + Math.max(0, duplicateDocumentCount - 1);
      const documentsMissing = Math.max(0, totalDocsExpected - documentsFound);

      const unidentifiedDocuments = Array(documentsMissing).fill({
        lithocode: 'unidentified',
        sortKey: 'document-unidentified',
        error: 'no pages of this document could be identified.',
      });

      const duplicateLithocodes = duplicateDocuments.map((document) => document['lithocode']);

      const current = results.current
        .filter((document) => {
          return duplicateLithocodes.indexOf(document.lithocode) === -1;
        })
        .concat(unidentifiedDocuments)
        .concat(duplicateDocuments);

      return of(new ResultsDataReceived(current));
    }),
  ));

}
